import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Calendar,
  ChevronLeft,
  Clock,
  MapPin,
  ArrowRight,
  List,
  ChevronUp,
} from "lucide-react";
import { getPublicArticle } from "@/lib/api";

// Parse markdown-style content into sections
function parseContent(contenu) {
  if (!contenu) return { intro: "", sections: [] };

  const lines = contenu.split("\n");
  let intro = "";
  const sections = [];
  let currentSection = null;

  for (const line of lines) {
    const headingMatch = line.match(/^##\s+(.+)/);
    if (headingMatch) {
      if (currentSection) sections.push(currentSection);
      const title = headingMatch[1].trim();
      currentSection = {
        title,
        id: title
          .toLowerCase()
          .replace(/[^a-zà-ÿ0-9\s]/g, "")
          .replace(/\s+/g, "-")
          .substring(0, 50),
        body: "",
      };
    } else if (currentSection) {
      currentSection.body += (currentSection.body ? "\n" : "") + line;
    } else {
      intro += (intro ? "\n" : "") + line;
    }
  }
  if (currentSection) sections.push(currentSection);

  return { intro: intro.trim(), sections };
}

// Estimate reading time
function readingTime(text) {
  const words = text.split(/\s+/).length;
  return Math.max(1, Math.ceil(words / 200));
}

// Render markdown-ish text to styled JSX
function RenderBody({ text }) {
  if (!text) return null;
  const lines = text.split("\n");
  const elements = [];
  let listItems = [];

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={`ul-${elements.length}`} className="space-y-2 my-3">
          {listItems.map((li, j) => (
            <li key={j} className="flex items-start gap-2.5 text-[#2D2418]/65 text-[15px]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C9922A] mt-2 flex-shrink-0" />
              <span dangerouslySetInnerHTML={{ __html: parseBold(li) }} />
            </li>
          ))}
        </ul>
      );
      listItems = [];
    }
  };

  const parseBold = (str) =>
    str.replace(/\*\*(.+?)\*\*/g, '<strong class="text-[#2D2418]/80 font-medium">$1</strong>');

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) {
      flushList();
      continue;
    }

    // Sub-heading (###)
    const h3Match = line.match(/^###\s+(.+)/);
    if (h3Match) {
      flushList();
      elements.push(
        <h3 key={`h3-${i}`} className="font-display text-base font-semibold text-[#004333] mt-5 mb-2">
          {h3Match[1]}
        </h3>
      );
      continue;
    }

    // List item (- )
    const liMatch = line.match(/^[-•]\s+(.+)/);
    if (liMatch) {
      listItems.push(liMatch[1]);
      continue;
    }

    // Normal paragraph
    flushList();
    elements.push(
      <p
        key={`p-${i}`}
        className="text-[#2D2418]/65 leading-relaxed text-[15px] mb-2"
        dangerouslySetInnerHTML={{ __html: parseBold(line) }}
      />
    );
  }
  flushList();
  return <>{elements}</>;
}

// Section colors for visual variety
const SECTION_ACCENTS = [
  { bg: "bg-[#004333]/5", border: "border-[#004333]/15", dot: "bg-[#004333]" },
  { bg: "bg-[#C9922A]/5", border: "border-[#C9922A]/15", dot: "bg-[#C9922A]" },
  { bg: "bg-[#004333]/5", border: "border-[#004333]/15", dot: "bg-[#004333]" },
  { bg: "bg-[#C9922A]/5", border: "border-[#C9922A]/15", dot: "bg-[#C9922A]" },
];

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    const loadArticle = async () => {
      try {
        const res = await getPublicArticle(slug);
        setArticle(res.data);
      } catch (error) {
        console.error("Error loading article:", error);
      } finally {
        setLoading(false);
      }
    };
    loadArticle();
  }, [slug]);

  // Scroll spy for TOC
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 600);

      if (!article) return;
      const { sections } = parseContent(article.contenu);
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i].id);
        if (el && el.getBoundingClientRect().top <= 150) {
          setActiveSection(sections[i].id);
          return;
        }
      }
      setActiveSection(null);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [article]);

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F0EB]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-[#004333]/20 border-t-[#004333] rounded-full animate-spin" />
          <span className="text-sm text-[#2D2418]/50">Chargement...</span>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F0EB]">
        <div className="text-center">
          <h2 className="font-display text-2xl text-[#004333] mb-4">
            Article non trouvé
          </h2>
          <Link to="/blog" className="btn-primary">
            Retour au blog
          </Link>
        </div>
      </div>
    );
  }

  const { intro, sections } = parseContent(article.contenu);
  const minutes = readingTime(article.contenu);
  const hasTOC = sections.length >= 3;

  return (
    <div className="bg-[#F5F0EB]" data-testid="article-detail">
      {/* ===================== HERO BANNER ===================== */}
      <section className="relative min-h-[50vh] md:min-h-[55vh] flex items-end overflow-hidden">
        {article.image && (
          <img
            src={article.image}
            alt={article.titre}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        {/* Overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1008]/90 via-[#1a1008]/40 to-[#1a1008]/10" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#004333]/30 to-transparent" />

        <div className="relative w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 pt-32">
          {/* Breadcrumb */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-white/60 hover:text-[#C9922A] transition-colors text-sm mb-6"
              data-testid="article-back-btn"
            >
              <ChevronLeft className="w-4 h-4" />
              Retour au blog
            </Link>
          </motion.div>

          {/* Meta */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="flex flex-wrap items-center gap-4 mb-4"
          >
            <span className="inline-flex items-center gap-1.5 text-white/60 text-xs">
              <Calendar className="w-3.5 h-3.5" />
              {formatDate(article.created_at)}
            </span>
            <span className="inline-flex items-center gap-1.5 text-white/60 text-xs">
              <Clock className="w-3.5 h-3.5" />
              {minutes} min de lecture
            </span>
            <span className="inline-flex items-center gap-1.5 text-white/60 text-xs">
              <MapPin className="w-3.5 h-3.5" />
              Guide local
            </span>
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight max-w-4xl"
          >
            {article.titre}
          </motion.h1>
        </div>
      </section>

      {/* ===================== CONTENT AREA ===================== */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className={`flex gap-12 ${hasTOC ? "lg:flex-row" : ""} flex-col`}>

          {/* Table of Contents — Sidebar */}
          {hasTOC && (
            <aside className="hidden lg:block lg:w-64 flex-shrink-0">
              <div className="sticky top-28">
                <div className="bg-white rounded-xl border border-[#E0D6CA]/60 shadow-sm p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <List className="w-4 h-4 text-[#C9922A]" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-[#2D2418]/40">
                      Sommaire
                    </span>
                  </div>
                  <nav className="space-y-1" data-testid="article-toc">
                    {sections.map((s, i) => (
                      <button
                        key={s.id}
                        onClick={() => scrollToSection(s.id)}
                        className={`block w-full text-left px-3 py-2 rounded-lg text-[13px] leading-snug transition-all duration-200 ${
                          activeSection === s.id
                            ? "bg-[#004333]/8 text-[#004333] font-medium"
                            : "text-[#2D2418]/50 hover:text-[#004333] hover:bg-[#004333]/4"
                        }`}
                        data-testid={`toc-item-${i}`}
                      >
                        <span className="text-[#C9922A] font-mono text-[11px] mr-1.5">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        {s.title.replace(/^\d+\.\s*/, "")}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            </aside>
          )}

          {/* Main Content */}
          <div className="flex-1 min-w-0" ref={contentRef}>
            {/* Intro */}
            {intro && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-12"
              >
                {intro.trim().startsWith("<") ? (
                  <div
                    className="article-html-content text-[#2D2418]/80 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: intro }}
                  />
                ) : (
                  <p className="text-lg md:text-xl text-[#2D2418]/70 leading-relaxed max-w-3xl font-light">
                    {intro}
                  </p>
                )}
              </motion.div>
            )}

            {/* Mobile TOC */}
            {hasTOC && (
              <div className="lg:hidden mb-10">
                <details className="bg-white rounded-xl border border-[#E0D6CA]/60 shadow-sm overflow-hidden">
                  <summary className="px-5 py-4 flex items-center gap-2 cursor-pointer text-sm font-semibold text-[#004333]">
                    <List className="w-4 h-4 text-[#C9922A]" />
                    Sommaire ({sections.length} sections)
                  </summary>
                  <div className="px-5 pb-4 space-y-1">
                    {sections.map((s, i) => (
                      <button
                        key={s.id}
                        onClick={() => scrollToSection(s.id)}
                        className="block w-full text-left px-3 py-2 rounded-lg text-[13px] text-[#2D2418]/60 hover:text-[#004333] hover:bg-[#004333]/4 transition-colors"
                      >
                        <span className="text-[#C9922A] font-mono text-[11px] mr-1.5">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        {s.title.replace(/^\d+\.\s*/, "")}
                      </button>
                    ))}
                  </div>
                </details>
              </div>
            )}

            {/* Sections */}
            <div className="space-y-8">
              {sections.map((section, i) => {
                const accent = SECTION_ACCENTS[i % SECTION_ACCENTS.length];
                return (
                  <motion.article
                    key={section.id}
                    id={section.id}
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.5, delay: 0.05 }}
                    className={`scroll-mt-28 ${accent.bg} rounded-2xl border ${accent.border} p-6 md:p-8 transition-shadow hover:shadow-md`}
                    data-testid={`article-section-${i}`}
                  >
                    {/* Section header */}
                    <div className="flex items-start gap-4 mb-4">
                      <div className={`w-10 h-10 rounded-xl ${accent.dot} flex items-center justify-center flex-shrink-0`}>
                        <span className="text-white font-bold text-sm font-mono">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                      </div>
                      <h2 className="font-display text-xl md:text-2xl text-[#004333] leading-snug pt-1.5">
                        {section.title.replace(/^\d+\.\s*/, "")}
                      </h2>
                    </div>

                    {/* Section body */}
                    <div className="pl-14">
                      <RenderBody text={section.body.trim()} />
                    </div>
                  </motion.article>
                );
              })}
            </div>


          </div>
        </div>
      </section>

      {/* ===================== CTA SECTION ===================== */}
      <section className="bg-[#004333] py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="text-[#C9922A] uppercase tracking-[0.2em] text-xs font-semibold mb-3 block">
              Prêt pour l'aventure ?
            </span>
            <h2 className="font-display text-2xl md:text-3xl text-white mb-4 leading-snug">
              Découvrez nos logements et réservez votre séjour
            </h2>
            <p className="text-white/60 mb-8 max-w-2xl mx-auto text-[15px]">
              Happyhouse conciergerie vous propose des logements soigneusement décorés et
              équipés pour un séjour inoubliable. Accueil personnalisé, linge de
              maison inclus, et nos meilleures adresses locales.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/voyageurs"
                className="btn-hero-outline inline-flex items-center justify-center gap-2.5"
                data-testid="article-cta-logements"
              >
                Voir nos logements
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                className="btn-light inline-flex items-center justify-center gap-2.5"
                data-testid="article-cta-contact"
              >
                Nous contacter
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Back to top button */}
      {showBackToTop && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-24 left-6 z-40 w-10 h-10 rounded-full bg-white shadow-lg border border-[#E0D6CA]/60 flex items-center justify-center text-[#004333] hover:bg-[#004333] hover:text-white transition-colors"
          data-testid="back-to-top-btn"
        >
          <ChevronUp className="w-5 h-5" />
        </motion.button>
      )}
    </div>
  );
}
